import { cloneDeep } from 'lodash';
import { IActionResponse, IUploadFileResponse } from '../../dal/BaseDAL';
import { ICollectionResponse } from '../../models/ICollectionResponse';
import { ILookupConditions } from '../../models/LookupConditions';
import { IAddDemandToProcurementOrderRequest, ICreateProcurementDemandItemRequest, ICreateProcurementOrderRequest, IGetProcurementOrderCostResponse, IGetProcurementQuestionResponse, IPlaceProcurementOrderRequest, IProcurementActivity, IProcurementDemandItem, IProcurementOrder, IProcurementSettings, IUpdateProcurementDemandItemRequest, IUpdateProcurementOrderRequest } from '../../models/Procurement';
import api from '../Api';

const procurementApi = api.injectEndpoints({
    endpoints: (build) => ({
        getProcurementOrders: build.query<ICollectionResponse<IProcurementOrder>, {conditions?: ILookupConditions}>({
            query: (arg) => ({
                url: 'procurement/orders/search',
                body: arg.conditions || {},
                method: 'POST'
            })
        }),
        getProcurementOrder: build.query<IProcurementOrder, {orderId: string}>({
            query: (arg) => `procurement/orders/${arg.orderId}`
        }),
        deleteProcurementOrder: build.mutation<void, {orderId: string}>({
            query: (arg) => ({
                url: `procurement/orders/${arg.orderId}`,
                method: 'DELETE'
            })
        }),
        getProcurementOrderQuestions: build.query<IGetProcurementQuestionResponse, {orderId: string}>({
            query: (arg) => `procurement/orders/${arg.orderId}/questions`
        }),
        getProcurementOrderCost: build.query<IGetProcurementOrderCostResponse, {orderId: string}>({
            query: (arg) => `procurement/orders/${arg.orderId}/cost`
        }),
        createProcurementOrder: build.mutation<IActionResponse, {order: ICreateProcurementOrderRequest}>({
            query: (arg) => ({
                url: 'procurement/orders',
                method: 'POST',
                body: arg.order
            })
        }),
        placeProcurementOrder: build.mutation<IActionResponse, {orderId: string, data: IPlaceProcurementOrderRequest}>({
            query: (arg) => ({
                url: `procurement/orders/${arg.orderId}/place`,
                method: 'POST',
                body: arg.data
            })
        }),
        updateProcurementOrder: build.mutation<IActionResponse, {orderId: string, order: IUpdateProcurementOrderRequest}>({
            query: (arg) => ({
                url: `procurement/orders/${arg.orderId}`,
                method: 'PATCH',
                body: arg.order
            }),
            onQueryStarted: async (arg, {dispatch, queryFulfilled}) => {
                const patchResult = dispatch(
                    procurementApi.util.updateQueryData('getProcurementOrder', {orderId: arg.orderId}, draft => {
                        if (!draft) return;
                        const copy = cloneDeep(draft)
                        for (const key in arg.order) {
                            if (key === 'itemAnswers') continue;
                            if (arg.order[key as keyof IUpdateProcurementOrderRequest] !== undefined) {
                                copy[key as keyof IProcurementOrder] = arg.order[key as keyof IUpdateProcurementOrderRequest] as never;
                            }
                        }
                        if (arg.order.itemAnswers) {
                            arg.order.itemAnswers.forEach(itemAnswer => {
                                const item = copy.items.find(i => i.id === itemAnswer.itemId);
                                if (item) item.answers = itemAnswer.answers;
                            });
                        }
                        Object.assign(draft, copy);
                    })
                );
                try {
                    const result = await queryFulfilled;
                    if (!result.data.success) {
                        patchResult.undo();
                    }
                } catch {
                    patchResult.undo();
                }
            }
        }),
        getProcurementOrderActivities: build.query<ICollectionResponse<IProcurementActivity>, {orderId: string, count?: number}>({
            query: (arg) => `procurement/orders/${arg.orderId}/activities` + (arg.count ? `?count=${arg.count}` : '')
        }),
        getProcurementOrderCountsByStatus: build.query<{[status: string]: number}, {vendor?: string}>({
            query: (arg) => `procurement/orders/reporting/countByStatus?vendor=${arg.vendor || ''}`
        }),
        getProcurementOrderCountsByVendor: build.query<{[status: string]: number}, void>({
            query: () => 'procurement/orders/reporting/countByVendor'
        }),
        createProcurementDemandItem: build.mutation<IActionResponse, {item: ICreateProcurementDemandItemRequest}>({
            query: (arg) => ({
                url: 'procurement/demands/items',
                method: 'POST',
                body: arg.item
            })
        }),
        getProcurementDemandItems: build.query<ICollectionResponse<IProcurementDemandItem>, {conditions?: ILookupConditions}>({
            query: (arg) => ({
                url: 'procurement/demands/items/search',
                body: arg.conditions || {},
                method: 'POST'
            })
        }),
        getProcurementDemandItem: build.query<IProcurementDemandItem, {itemId: string}>({
            query: (arg) => `procurement/demands/items/${arg.itemId}`
        }),
        updateProcurementDemandItem: build.mutation<IActionResponse, {itemId: string, update: IUpdateProcurementDemandItemRequest}>({
            query: (arg) => ({
                url: `procurement/demands/items/${arg.itemId}`,
                method: 'PATCH',
                body: arg.update
            })
        }),
        deleteProcurementDemandItem: build.mutation<void, {itemId: string}>({
            query: (arg) => ({
                url: `procurement/demands/items/${arg.itemId}`,
                method: 'DELETE'
            })
        }),
        addProcurementDemandItemToOrder: build.mutation<IActionResponse, {itemId: string, orderId: string, data: IAddDemandToProcurementOrderRequest}>({
            query: (arg) => ({
                url: `procurement/demands/items/${arg.itemId}/addToOrder/${arg.orderId}`,
                method: 'POST',
                body: arg.data
            })
        }),
        getProcurementDemandItemActivities: build.query<ICollectionResponse<IProcurementActivity>, {itemId: string, count?: number}>({
            query: (arg) => `procurement/demands/items/${arg.itemId}/activities` + (arg.count ? `?count=${arg.count}` : '')
        }),
        getProcurementDemandItemCountsByStatus: build.query<{[status: string]: number}, void>({
            query: () => 'procurement/demands/items/reporting/countByStatus'
        }),
        getProcurementDemandItemCountsByVendor: build.query<{[status: string]: number}, void>({
            query: () => 'procurement/demands/items/reporting/countByVendor'
        }),
        getRecentProcurementActivities: build.query<ICollectionResponse<IProcurementActivity>, {count?: number}>({
            query: (arg) => `procurement/activities/recent` + (arg.count ? `?count=${arg.count}` : '')
        }),
        getProcurementOrganizationSettings: build.query<IProcurementSettings, void>({
            query: () => 'procurement/settings/organization'
        }),
        updateProcurementOrganizationSettings: build.mutation<IProcurementSettings, {settings: IProcurementSettings}>({
            query: (arg) => ({
                url: 'procurement/settings/organization',
                method: 'PUT',
                body: arg.settings
            })
        }),
        getProcurementVendorSettings: build.query<IProcurementSettings, {vendorId: string}>({
            query: (arg) => `procurement/settings/vendor/${arg.vendorId}`
        }),
        updateProcurementVendorSettings: build.mutation<IProcurementSettings, {vendorId: string, settings: IProcurementSettings}>({
            query: (arg) => ({
                url: `procurement/settings/vendor/${arg.vendorId}`,
                method: 'PUT',
                body: arg.settings
            })
        }),
        generateProcurementOrderCsv: build.mutation<IUploadFileResponse, {orderId: string}>({
            query: (arg) => ({
                url: `procurement/orders/${arg.orderId}/csv`,
                method: 'POST'
            })
        }),
        updateProcurementOrderStatus: build.mutation<IActionResponse, {orderId: string, status: string}>({
            query: (arg) => ({
                url: `procurement/orders/${arg.orderId}/status`,
                method: 'PATCH',
                body: { status: arg.status }
            })
        }),
        fetchProcurementOrderFromVendor: build.mutation<IActionResponse, {orderId: string}>({
            query: (arg) => ({
                url: `procurement/orders/${arg.orderId}/fetchFromVendor`,
                method: 'POST'
            })
        }),
        createCrmPurchaseOrder: build.mutation<IActionResponse, {orderId: string}>({
            query: (arg) => ({
                url: `procurement/orders/${arg.orderId}/createCrmPurchaseOrder`,
                method: 'POST'
            })
        }),
    }),
    overrideExisting: false,
});

export const {
    useGetProcurementOrdersQuery,
    useLazyGetProcurementOrdersQuery,
    useGetProcurementOrderQuery,
    useCreateProcurementOrderMutation,
    useDeleteProcurementOrderMutation,
    useGetProcurementOrderActivitiesQuery,
    useGetProcurementOrderCountsByStatusQuery,
    useGetProcurementOrderCountsByVendorQuery,
    useGetProcurementOrderQuestionsQuery,
    useGetProcurementOrderCostQuery,
    useCreateProcurementDemandItemMutation,
    usePlaceProcurementOrderMutation,
    useUpdateProcurementOrderMutation,
    useGetProcurementDemandItemsQuery,
    useGetProcurementDemandItemQuery,
    useLazyGetProcurementDemandItemQuery,
    useUpdateProcurementDemandItemMutation,
    useDeleteProcurementDemandItemMutation,
    useAddProcurementDemandItemToOrderMutation,
    useGetProcurementDemandItemActivitiesQuery,
    useGetProcurementDemandItemCountsByStatusQuery,
    useGetProcurementDemandItemCountsByVendorQuery,
    useGetRecentProcurementActivitiesQuery,
    useGetProcurementOrganizationSettingsQuery,
    useUpdateProcurementOrganizationSettingsMutation,
    useGetProcurementVendorSettingsQuery,
    useUpdateProcurementVendorSettingsMutation,
    useGenerateProcurementOrderCsvMutation,
    useUpdateProcurementOrderStatusMutation,
    useFetchProcurementOrderFromVendorMutation,
    useCreateCrmPurchaseOrderMutation,
} = procurementApi;